/**
 * Pure adapters that assemble the Affiliates roster + overview view-models from
 * the partner API pieces. Kept free of React/axios so they unit-test in isolation.
 *
 * An affiliate on the platform is an identity (`/affiliates`); its numbers and its
 * deal shape come from the reports (`/reports/performance|cpa|commissions`). We join
 * them here by `affiliate_id`. Any figure the reports don't supply renders with the
 * area's placeholder glyphs (`?` not wired · `N/A` failing · `–` divide-by-zero),
 * never a fabricated value.
 */
import type { StatTone } from '../../components/StatCell/StatCell';
import type {
  Affiliate,
  AffiliateCampaign,
  AffiliateCommissionRow,
  AffiliateCommissionsReport,
  AffiliateCpaReport,
  AffiliateCpaRow,
  AffiliateDealType,
  AffiliateMetrics,
  AffiliatePerformance,
  CommissionPlan,
  CommissionPlanType,
  CommissionStructure,
} from './types';

/** A figure that either resolved to a real value or carries a reason it did not. */
type FigureState = 'ok' | 'missing' | 'error' | 'indeterminate';

const glyphFor = (state: Exclude<FigureState, 'ok'>): string =>
  state === 'missing' ? '?' : state === 'error' ? 'N/A' : '–';

export interface AffiliateStatVM {
  id: string;
  label: string;
  value: string;
  tone?: StatTone;
}

export interface AffiliateCardVM {
  id: string;
  name: string;
  email: string;
  /** Normalised to the two states the UI toggles; `active` false → the affiliate is paused. */
  active: boolean;
  type?: AffiliateDealType;
  typeLabel: string;
  typeDetail?: string;
  /** The commission plan ASSIGNED to this affiliate (from `/commission-structure`), if any. This is
   *  what the roster shows + filters by — distinct from `type`, which is derived from report activity. */
  planName?: string;
  planType?: CommissionPlanType;
  stats: AffiliateStatVM[];
  affiliate: Affiliate;
}

export interface OverviewMetricVM {
  id: string;
  label: string;
  value: string;
}

// --- formatters -------------------------------------------------------------

const hasNumber = (n: number | undefined | null): n is number => typeof n === 'number' && Number.isFinite(n);

const withThousands = (n: number): string => Math.round(n).toLocaleString('en-US');

/** `1,234` (integer with thousands) for counts. */
export const formatCount = (n: number): string => withThousands(n);

/** `$1,234` (integer) for totals. */
export const formatMoney = (n: number): string => `$${withThousands(n)}`;

/**
 * Short "time until expiry" for a pending invite, from its `expires_at` (RFC 3339). Returns the
 * largest whole unit ("5d" / "3h" / "12m"), `{ expired: true }` once past, or null when there is no
 * usable date. `nowMs` is passed in (Date.now() at render) to keep the function pure/testable.
 */
export const expiresInShort = (
  expiresAt: string | undefined | null,
  nowMs: number,
): { text: string; expired: boolean } | null => {
  if (!expiresAt) return null;
  const ms = Date.parse(expiresAt);
  if (Number.isNaN(ms)) return null;
  const diffMin = Math.floor((ms - nowMs) / 60000);
  if (diffMin <= 0) return { text: '', expired: true };
  if (diffMin >= 1440) return { text: `${Math.floor(diffMin / 1440)}d`, expired: false };
  if (diffMin >= 60) return { text: `${Math.floor(diffMin / 60)}h`, expired: false };
  return { text: `${diffMin}m`, expired: false };
};

/** `$26.3` (one decimal) for per-unit ratios that read better with precision. */
export const formatMoneyPrecise = (n: number): string =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;

/** `33.8%`, one decimal. */
export const formatPercent1 = (n: number): string => `${n.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

/** `233%`, integer, signed via the caller. */
export const formatPercent0 = (n: number): string => `${Math.round(n)}%`;

/** Resolve a raw number to a display string, or the right glyph when it is absent/undefined. */
const figure = (
  n: number | undefined | null,
  format: (v: number) => string,
  whenMissing: Exclude<FigureState, 'ok'> = 'missing',
): string => (hasNumber(n) ? format(n) : glyphFor(whenMissing));

/** A ratio a/b guarding divide-by-zero (`–`) and a missing numerator/denominator (`?`). */
const ratio = (a: number | undefined | null, b: number | undefined | null, format: (v: number) => string): string => {
  if (!hasNumber(a) || !hasNumber(b)) return glyphFor('missing');
  if (b === 0) return glyphFor('indeterminate');
  return format(a / b);
};

/**
 * Reg → FTD %: derived from the counts (FTDs / Registrations), NOT read from the report's
 * precomputed `conversion` field. Computing it ourselves keeps the scale deterministic (the wire
 * `conversion` is `null` this period and its numeric scale — 0-1 vs 0-100 — is unconfirmed), and a
 * zero-registration affiliate then reads as `–` (a real divide-by-zero this period), not `?`.
 */
const regToFtd = (m: AffiliateMetrics | undefined): string =>
  ratio(m?.ftds, m?.registrations, (v) => formatPercent1(v * 100));

// --- indexing ---------------------------------------------------------------

/** Index the per-affiliate metric blocks by affiliate id. The wire row is `{ affiliate, metrics }`. */
const perfByAffiliate = (perf: AffiliatePerformance | undefined): Map<string, AffiliateMetrics> => {
  const map = new Map<string, AffiliateMetrics>();
  for (const row of perf?.rows ?? []) {
    const id = row.affiliate?.id;
    if (id && row.metrics) map.set(id, row.metrics);
  }
  return map;
};

/** CPA report can carry one row per brand for an affiliate; fold them (sum cost, keep the first deal seen). */
const cpaByAffiliate = (cpa: AffiliateCpaReport | undefined): Map<string, { cpaUsd: number; deal?: AffiliateCpaRow['deal'] }> => {
  const map = new Map<string, { cpaUsd: number; deal?: AffiliateCpaRow['deal'] }>();
  for (const row of cpa?.affiliates ?? []) {
    const id = row.affiliate?.id;
    if (!id) continue;
    const prev = map.get(id) ?? { cpaUsd: 0 };
    if (hasNumber(row.cpa_usd)) prev.cpaUsd += row.cpa_usd;
    if (!prev.deal && row.deal) prev.deal = row.deal;
    map.set(id, prev);
  }
  return map;
};

const commissionByAffiliate = (report: AffiliateCommissionsReport | undefined): Map<string, AffiliateCommissionRow> => {
  const map = new Map<string, AffiliateCommissionRow>();
  for (const row of report?.affiliates ?? []) {
    const id = row.affiliate?.id;
    if (id) map.set(id, row);
  }
  return map;
};

// --- deal type --------------------------------------------------------------

/**
 * Derive the deal shape from the commission split (authoritative when present),
 * falling back to the CPA report's plan hint. Returns undefined when nothing says.
 */
export function deriveDealType(
  commission: AffiliateCommissionRow | undefined,
  cpa: { cpaUsd: number; deal?: AffiliateCpaRow['deal'] } | undefined,
): AffiliateDealType | undefined {
  const rev = commission?.revshare_usd ?? 0;
  const cpaAmount = (commission?.cpa_usd ?? 0) || (cpa?.cpaUsd ?? 0);
  if (rev > 0 && cpaAmount > 0) return 'hybrid';
  if (cpaAmount > 0) return 'cpa';
  if (rev > 0) return 'rev-share';
  const hint = cpa?.deal?.type?.toLowerCase();
  if (hint) {
    if (hint.includes('hybrid')) return 'hybrid';
    if (hint.includes('cpa')) return 'cpa';
    if (hint.includes('rev')) return 'rev-share';
  }
  return undefined;
}

const DEAL_LABEL: Record<AffiliateDealType, string> = { cpa: 'CPA', 'rev-share': 'Rev Share', hybrid: 'Hybrid' };

/**
 * The commission plan ASSIGNED to an affiliate, pulled from its `/commission-structure`. Prefers the
 * "all brands" default (brand_id null); else the first direct assignment. Group-inherited assignments
 * are not shown here (the editor only manages the affiliate's own). Undefined when nothing is assigned.
 */
export function assignedPlan(
  structure: CommissionStructure | undefined,
): { name: string; type: CommissionPlanType } | undefined {
  const assignments = structure?.assignments ?? [];
  if (assignments.length === 0) return undefined;
  const primary = assignments.find((a) => !a.brand_id) ?? assignments[0];
  const plan = primary?.plan;
  return plan ? { name: plan.name, type: plan.type } : undefined;
}

/**
 * The plans already in use across the roster, one per plan id in roster order: each affiliate's "all brands" default
 * (the only kind this page assigns), so the owner can give another affiliate the same deal instead of creating a
 * duplicate plan. Brand-scoped assignments are console-made and left out. The vendor has no plan list, so this is
 * the only way to find existing plans. Pure.
 */
export function reusablePlans(structures: ReadonlyMap<string, CommissionStructure | undefined> | undefined): CommissionPlan[] {
  const plans: CommissionPlan[] = [];
  for (const structure of structures?.values() ?? []) {
    for (const assignment of structure?.assignments ?? []) {
      const plan = assignment.plan;
      if (assignment.brand_id || !plan?.id || plans.some((p) => p.id === plan.id)) continue;
      plans.push(plan);
    }
  }
  return plans;
}

// --- roster card ------------------------------------------------------------

/** A partner affiliate counts as paused when `active` is explicitly false or status maps to a rejected/inactive state. */
export function isAffiliateActive(affiliate: Affiliate): boolean {
  if (typeof affiliate.active === 'boolean') return affiliate.active;
  const status = affiliate.status?.toLowerCase();
  if (!status) return true;
  return !(status === 'inactive' || status === 'rejected' || status === 'paused');
}

/**
 * Whether a campaign is active (vs paused). The partner API returns `status: 'paused'` for a
 * paused campaign (the status toggle SENDS `'inactive'`, which the backend maps to `'paused'`),
 * so a predicate that only checks `'inactive'` misreads a paused campaign as active. A missing
 * status defaults to active. Single source of truth for the campaigns list badge and the form's
 * status chip.
 */
export function isCampaignActive(campaign: AffiliateCampaign): boolean {
  const status = campaign.status?.toLowerCase();
  if (!status) return true;
  return !(status === 'inactive' || status === 'paused');
}

export function buildAffiliateCard(
  affiliate: Affiliate,
  perf: AffiliateMetrics | undefined,
  cpa: { cpaUsd: number; deal?: AffiliateCpaRow['deal'] } | undefined,
  commission: AffiliateCommissionRow | undefined,
  structure?: CommissionStructure,
): AffiliateCardVM {
  const type = deriveDealType(commission, cpa);
  const plan = assignedPlan(structure);
  const totalCost = commission?.commission_usd;
  const ftds = perf?.ftds;
  const ngr = perf?.ngr_usd;

  const stats: AffiliateStatVM[] = [
    { id: 'regs', label: 'Regs', value: figure(perf?.registrations, withThousands) },
    { id: 'ftds', label: 'FTDs', value: figure(ftds, withThousands) },
    { id: 'reg-ftd', label: 'Reg → FTD', value: regToFtd(perf) },
    { id: 'avg-dep', label: 'Avg Dep', value: ratio(perf?.deposits_usd, ftds, formatMoney) },
  ];

  // Cost/ROI figures only exist once commissions are in; otherwise leave them off the card
  // rather than filling a 4-tile row with `?`s.
  if (commission) {
    const roiValue: string = hasNumber(totalCost) && hasNumber(ngr)
      ? totalCost === 0
        ? glyphFor('indeterminate')
        : formatPercent0(((ngr - totalCost) / totalCost) * 100)
      : glyphFor('missing');
    const roiTone: StatTone | undefined = hasNumber(totalCost) && hasNumber(ngr) && totalCost !== 0
      ? ngr - totalCost >= 0
        ? 'positive'
        : 'negative'
      : undefined;
    stats.push(
      { id: 'cost-ftd', label: 'Cost / FTD', value: ratio(totalCost, ftds, formatMoneyPrecise) },
      { id: 'rev-ftd', label: 'Rev / FTD', value: ratio(ngr, ftds, formatMoneyPrecise) },
      { id: 'roi', label: 'ROI', value: roiValue, tone: roiTone },
      { id: 'total-cost', label: 'Total Cost', value: figure(totalCost, formatMoney) },
    );
  }

  const cpaAmount = cpa?.deal?.cpa_amount_usd;
  const typeDetail =
    type === 'cpa' || type === 'hybrid' ? (hasNumber(cpaAmount) ? `${formatMoney(cpaAmount)} / FTD` : undefined) : undefined;

  return {
    id: affiliate.id,
    name: affiliate.display_name?.trim() || affiliate.email,
    email: affiliate.email,
    active: isAffiliateActive(affiliate),
    type,
    typeLabel: type ? DEAL_LABEL[type] : 'Affiliate',
    typeDetail,
    planName: plan?.name,
    planType: plan?.type,
    stats,
    affiliate,
  };
}

/** A campaign's affiliate activity over the stats window, matched by campaign id (see useCampaignStats). */
export interface CampaignStat {
  registrations: number;
  ftds: number;
}

/**
 * The Regs / FTDs / Reg -> FTD tiles for a campaign card, using the SAME formatting as the roster
 * stats. A loaded campaign shows its real figures (zeros when it had no activity in the window, like
 * the video-ads grid). `undefined` means the stats read has not settled or failed for it, and renders
 * the placeholder glyphs, matching how the roster shows a missing figure.
 */
export function buildCampaignStats(stat: CampaignStat | undefined): AffiliateStatVM[] {
  return [
    { id: 'regs', label: 'Regs', value: figure(stat?.registrations, withThousands) },
    { id: 'ftds', label: 'FTDs', value: figure(stat?.ftds, withThousands) },
    { id: 'reg-ftd', label: 'Reg → FTD', value: ratio(stat?.ftds, stat?.registrations, (v) => formatPercent1(v * 100)) },
  ];
}

/** Join the affiliate list with the three reports into roster cards (identity order preserved). */
export function buildRoster(
  affiliates: Affiliate[],
  perf: AffiliatePerformance | undefined,
  cpa: AffiliateCpaReport | undefined,
  commissions: AffiliateCommissionsReport | undefined,
  structures?: Map<string, CommissionStructure | undefined>,
): AffiliateCardVM[] {
  const perfIdx = perfByAffiliate(perf);
  const cpaIdx = cpaByAffiliate(cpa);
  const commIdx = commissionByAffiliate(commissions);
  return affiliates.map((affiliate) =>
    buildAffiliateCard(
      affiliate,
      perfIdx.get(affiliate.id),
      cpaIdx.get(affiliate.id),
      commIdx.get(affiliate.id),
      structures?.get(affiliate.id),
    ),
  );
}

// --- overview KPIs ----------------------------------------------------------

/**
 * "This month's Overview": Total Spend (the commissions report's own total), Blended CPA
 * (spend ÷ FTDs), plus Regs / FTDs from the performance totals. Total Spend reads the
 * report `totals.commission_usd` — the authoritative sum the backend already computes — so
 * it renders a real `$0` when the period is open with no accrual, not `?`. It stays `?`
 * (not wired) only when the whole commissions report is absent (a failed best-effort fetch).
 */
export function buildOverview(
  perf: AffiliatePerformance | undefined,
  commissions: AffiliateCommissionsReport | undefined,
): OverviewMetricVM[] {
  const totals = perf?.totals;
  const spend = commissions?.totals?.commission_usd;
  const ftds = totals?.ftds;
  return [
    { id: 'total-spend', label: 'Total Spend', value: figure(spend, formatMoney) },
    { id: 'blended-cpa', label: 'Blended CPA', value: ratio(spend, ftds, formatMoney) },
    { id: 'regs', label: 'Registrations', value: figure(totals?.registrations, withThousands) },
    { id: 'ftds', label: 'FTDs', value: figure(ftds, withThousands) },
  ];
}
